import { z } from "zod";

const numberValidation = z.preprocess(
    (val) => {
        if (typeof val === "string") {
            const trimmed = val.trim();
            if (trimmed === "") return undefined;
            const parsed = Number(trimmed);
            return isNaN(parsed) ? val : parsed;
        }
        return val;
    },
    z
        .number()
        .optional()
        .refine((val) => val === undefined || Number.isFinite(val), {
            message: "value must be a valid number",
        })
);
const VariationInfoSchema = z.object({
    size: z.object({ name: z.string().optional() }).optional(),
    weight: z
        .object({
            quantity: numberValidation.optional(),
            unit: z.string().optional(),
        })
        .optional(),
    color: z.object({ name: z.string().optional() }).optional(),
    flavor: z.object({ name: z.string().optional() }).optional(),
    imageList: z.array(z.string()).optional(),
    itemPackingQuantity: numberValidation.default(1).optional(),
});

const PriceSchema = z.object({
    mrp: numberValidation.refine(val => val !== undefined, {
        message: "MRP is required",
    }),
    purchasePrice: numberValidation.optional(),
    sellingPrice: numberValidation.optional(),
});

const StockSchema = z.object({
    productUnit: z.string().optional(),
    quantity: numberValidation.optional(),
    minimumAlertQuantity: numberValidation.optional(),
});

const MinimumQuantitySchema = z.object({
    quantity: numberValidation.optional().default(1),
    isMultiple: z.object({ enable: z.boolean().default(false) }).optional(),
});

const VariationSchema = z.object({
    price: PriceSchema.optional(),
    barcode: z
      .string()
      .optional()
      .refine(val => !val || val.length >= 3, {
        message: 'Barcode must be 3 or more characters if provided',
      }),
    productCode: z.string().optional(),
    variationInfo: VariationInfoSchema.optional(),
    stock: StockSchema.optional(),
    minimumQuantity: MinimumQuantitySchema.optional(),
});

export const ProductSchema = z.object({
    _id: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    brandId: z.string().optional(),
    categoryId: z.string().optional(),
    hsnCode: numberValidation.optional(),
    sellerProductDetail: VariationSchema.optional(),
    taxPercentage: numberValidation.optional(),
    imageList: z.array(z.string()).optional(),
    subCategoryId: z.string().optional(),
    mrp: numberValidation.optional(),
});

export type ProductForm = z.infer<typeof ProductSchema>;